import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, money, dateFR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, X, Loader2 } from "lucide-react";
import Plate from "@/components/Plate";
import StatusBadge from "@/components/StatusBadge";

/**
 * Live, debounced quick-search bar.
 * Hits /ventes/search/quick and shows a floating dropdown of matches.
 * Clicking a result navigates to /ventes/:id.
 */
export default function QuickSearchBar({ placeholder, testId = "quick-search", autoFocus = false }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const reqIdRef = useRef(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Debounced live search
  useEffect(() => {
    const query = q.trim();
    if (!query) {
      setResults([]);
      setLoading(false);
      setOpen(false);
      return;
    }
    setLoading(true);
    setOpen(true);
    const myReq = ++reqIdRef.current;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/ventes/search/quick", { params: { q: query } });
        if (myReq !== reqIdRef.current) return;
        setResults(data || []);
        setActiveIdx((data && data.length) ? 0 : -1);
      } catch {
        if (myReq !== reqIdRef.current) return;
        setResults([]);
        setActiveIdx(-1);
      } finally {
        if (myReq === reqIdRef.current) setLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [q]);

  const pick = (v) => {
    setOpen(false);
    setQ("");
    setResults([]);
    navigate(`/ventes/${v.id}`);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) {
      if (e.key === "Enter" && q.trim()) {
        e.preventDefault();
        navigate(`/plaques?q=${encodeURIComponent(q.trim())}`);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && results[activeIdx]) pick(results[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapRef} className="relative w-full" data-testid={testId}>
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
      <Input
        ref={inputRef}
        data-testid={`${testId}-input`}
        type="search"
        name="quick-search"
        autoComplete="off"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => { if (results.length > 0) setOpen(true); }}
        onKeyDown={onKeyDown}
        autoFocus={autoFocus}
        placeholder={placeholder || "Rechercher…"}
        className="pl-9 pr-9 rounded-sm h-10"
      />
      {loading && (
        <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-klein animate-spin" />
      )}
      {!loading && q && (
        <button
          type="button"
          onClick={() => { setQ(""); inputRef.current?.focus(); }}
          data-testid={`${testId}-clear`}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground hover:bg-slate-100 rounded-sm"
          aria-label="Effacer"
        >
          <X size={14} />
        </button>
      )}

      {open && q.trim() && (
        <div
          data-testid={`${testId}-dropdown`}
          className="absolute z-40 mt-1 w-full bg-popover border border-border rounded-sm shadow-xl max-h-96 overflow-y-auto"
        >
          {results.length === 0 && !loading && (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              Aucun résultat
            </div>
          )}
          {results.map((v, idx) => (
            <button
              type="button"
              key={v.id}
              data-testid={`${testId}-result-${v.id}`}
              onMouseEnter={() => setActiveIdx(idx)}
              onClick={() => pick(v)}
              className={`w-full text-left px-3 py-2.5 flex items-center justify-between gap-3 border-b last:border-b-0 border-border/60 ${
                idx === activeIdx ? "bg-slate-100 dark:bg-slate-800" : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <Plate value={v.plaque} small />
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">
                    {v.marque} {v.modele} {v.annee ? `(${v.annee})` : ""} — {v.client_nom}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {dateFR(v.date_vente)} · Prix {money(v.prix_vente)} · Restant {money(v.montant_restant)}
                  </div>
                </div>
              </div>
              <StatusBadge statut={v.statut} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
